import { isAfter, isEqual } from 'date-fns'
import debounce from 'lodash/debounce'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'

import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'
import { Checkbox, CheckboxGroup } from '@wartek-id/checkbox-v1'
import {
  Dialog,
  DialogAction,
  DialogCancel,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Input, InputGroup, InputLeftAddon } from '@wartek-id/input'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import {
  API_PATH_LIST_GURU_STATES,
  API_PATH_PREDIKAT_KINERJA_STEP,
  useAPIGetListGuruStates,
  useAPIGetPelaksanaanConfig,
  useAPIGetPenetapanPredikatKinerjaStep,
  useAPIPredikatKinerjaReset,
} from 'api/kinerja/useAPIKinerja'
import FilterButtons from 'app/KinerjaPage/components/FilterButtons'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
  TableShimmer,
} from 'app/KinerjaPage/components/TablePelaksanaanKinerja'
import useDemoContext from 'app/KinerjaPage/hooks/useDemoContext'
import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'
import { PENGELOLAAN_KINERJA_KS_PENILAI_PATH } from 'app/KinerjaShared/constants/path'
import { formatDate, isDateValid } from 'utils/datetime'
import { assetUrl } from 'utils/url'
import CardDeadlinePeriod from 'app/KinerjaShared/components/CardDeadlinePeriod'
import useResetToggle from 'app/KinerjaPage/hooks/useResetToggle'
import DialogFilledBySystem from 'app/KinerjaPage/pages/PelaksanaanV2/components/DialogFilledBySystem'
import DialogBlockPeriodNonAsn from 'app/KinerjaShared/components/DialogBlockPeriodNonAsn'
import useToggleSdkContext from 'utils/useToogleSdk'
import { sanitizeRegex } from 'utils/text'

import type { TStatePelaksanaanTranslated } from 'types/kinerja-pelaksanaan'

interface DaftarGuruProps {
  npsn: string
}

type FilterStatusDocumentValue = TStatePelaksanaanTranslated | 'all'

type FilterStatusDocument = {
  label: string
  value: FilterStatusDocumentValue
}

const NEW_GRADING_STATE_MAPPING = {
  Observasi: 'Rating observasi',
  'Rencana Tindak Lanjut': 'Nilai tindak lanjut',
  'Refleksi dan Evaluasi Tindak Lanjut': 'Nilai refleksi tindak lanjut',
  'Sudah Selesai': 'Sudah selesai',
} as const

// Reverse mapping for NEW_GRADING_STATE_MAPPING
const GRADING_STATE_DISPLAY_TO_INTERNAL_MAP = {
  ...Object.fromEntries(
    Object.entries(NEW_GRADING_STATE_MAPPING).map(([key, value]) => [
      value,
      key,
    ])
  ),
  'Belum dinilai': 'Belum Selesai',
  'Sudah dinilai': 'Sudah Selesai',
}

const mapNewGradingStateLabel = (state: string) => {
  return GRADING_STATE_DISPLAY_TO_INTERNAL_MAP[state] || state
}

export function DaftarGuru({ npsn }: DaftarGuruProps) {
  const toggle = useToggleSdkContext()

  const isBlockNonAsnEnabled = toggle?.['kinerja-block-non-asn-period'] === true
  const isPenetapanPredikatKinerjaEnabled =
    process.env.NEXT_PUBLIC_SKP_FORM_F_ENABLED === 'true'
  const [activeFilter, setCurrentActiveFilter] =
    useState<FilterStatusDocumentValue>('all')
  const [activeSearch, setCurrentActiveSearch] = useState('')
  const [preSearchText, setPreSearchText] = useState('')
  const [isFilledBySystemDialogOpen, setFilledBySystemDialogOpen] =
    useState(false)
  const [isBlockNonAsnDialogOpen, setBlockNonAsnDialogOpen] = useState(false)

  const { trackEvent } = useSKPPelaksanaanTrackEvent()
  const { enabled: resetEnabled } = useResetToggle()

  const { isDemoMode } = useDemoContext()

  const NO_DATE_VALUE = 'Belum ada jadwal'
  const ALREADY_GRADED_VALUE = 'sudah dinilai'
  const DONE_VALUE = 'sudah selesai'

  const {
    data: listGuruStatesData,
    isFetching: isLoadingListGuruStates,
    isSuccess,
  } = useAPIGetListGuruStates({
    npsn,
    options: {
      useErrorBoundary: true,
      staleTime: 60 * 60 * 1000, // 1 hour
    },
    isDemoMode,
  })

  const { data: dataPelaksanaanConfig } = useAPIGetPelaksanaanConfig({
    params: { key: 'PENETAPAN_PREDIKAT_KINERJA' },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
      enabled: isPenetapanPredikatKinerjaEnabled,
      // Disable error boundary since the data is not required for this page
      useErrorBoundary: false,
    },
  })

  // access date
  const accessDate = dataPelaksanaanConfig?.data?.predicatePerformanceAccessDate
  const isValidAccessDate = isDateValid(accessDate)
  const currentDate = new Date()
  const accessDateObject = new Date(accessDate)
  const isOpenAccess =
    isValidAccessDate &&
    (isAfter(currentDate, accessDateObject) ||
      isEqual(currentDate, accessDateObject))

  // deadline date
  const deadlineDate =
    dataPelaksanaanConfig?.data?.predicatePerformanceDeadlineDate
  const isValidDeadlineDate = isDateValid(deadlineDate)
  const formattedDeadlineDate = isValidDeadlineDate
    ? formatDate(deadlineDate)
    : '-'

  const filteredDataWithSearch = useMemo(() => {
    if (!isSuccess) return []
    if (activeFilter == 'all' && !activeSearch) return listGuruStatesData.data
    if (activeFilter != 'all') {
      return listGuruStatesData.data.filter(
        (thisData) =>
          thisData.observation.gradingState?.toLowerCase() ===
            activeFilter?.toLowerCase() ||
          thisData.observation.gradingState?.toLowerCase() ===
            NEW_GRADING_STATE_MAPPING[activeFilter].toLowerCase()
      )
    }

    if (activeSearch) {
      const searchRegex = new RegExp(sanitizeRegex(activeSearch), 'i')
      return listGuruStatesData.data.filter(
        (thisData) => thisData.name.search(searchRegex) !== -1
      )
    }
  }, [isSuccess, activeFilter, listGuruStatesData, activeSearch])

  const filterMeta: FilterStatusDocument[] = useMemo(() => {
    if (!isSuccess) return [{ label: 'Semua (0)', value: 'all' }]
    const { total, states } = listGuruStatesData.meta
    return [
      { label: `Semua (${total})`, value: 'all' },
      {
        label: `Observasi (${
          states['Observasi'] || states['Rating observasi'] || 0
        })`,
        value: 'Observasi',
      },
      {
        label: `Rencana Tindak Lanjut (${
          states['Rencana Tindak Lanjut'] || states['Nilai tindak lanjut'] || 0
        })`,
        value: 'Rencana Tindak Lanjut',
      },
      {
        label: `Refleksi dan Evaluasi Tindak Lanjut (${
          states['Refleksi dan Evaluasi Tindak Lanjut'] ||
          states['Nilai refleksi tindak lanjut'] ||
          0
        })`,
        value: 'Refleksi dan Evaluasi Tindak Lanjut',
      },
      {
        label: `Sudah Selesai (${
          states['Sudah Selesai'] || states['Sudah selesai'] || 0
        })`,
        value: 'Sudah Selesai',
      },
    ] as FilterStatusDocument[]
  }, [isSuccess, listGuruStatesData])

  const handleTrackerTypeTeacherName = useMemo(
    () =>
      debounce((e) => {
        if (e.target.value) {
          trackEvent({
            activity_type: 'penilaian_ks_periksa_skp_type_teacher_name',
            screen_name: 'ks_periksa_skp',
            event_trigger: 'ks_penilaian_hub',
          })
        }
      }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleTrackerFilter = (selectedFilter: FilterStatusDocumentValue) => {
    const filterToNavbarMap: Record<string, string> = {
      all: 'semua',
      Observasi: 'observasi',
      'Rencana Tindak Lanjut': 'tindak_lanjut',
      'Refleksi dan Evaluasi Tindak Lanjut': 'refleksi_tindak_lanjut',
      'Sudah Selesai': 'sudah_selesai',
      // for backward compatibility
      'Rating observasi': 'observasi',
      'Nilai tindak lanjut': 'tindak_lanjut',
      'Nilai refleksi tindak lanjut': 'refleksi_tindak_lanjut',
      'Sudah selesai': 'sudah_selesai',
    }
    const navbar = filterToNavbarMap[selectedFilter]

    if (navbar) {
      trackEvent({
        activity_type: 'penilaian_ks_periksa_skp_click_navbar',
        screen_name: 'ks_periksa_skp',
        event_trigger: 'ks_penilaian_hub',
        attributes: {
          navbar: [navbar, 'string'],
        },
      })
    }
  }

  useEffect(() => {
    trackEvent({
      activity_type: 'penilaian_ks_open_periksa_skp',
      screen_name: 'ks_periksa_skp',
      event_trigger: 'ks_penilaian_hub',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="px-8 pb-8 pt-12 bg-gray-10 min-h-full">
      <div className="max-w-7xl mx-auto pb-12">
        <Header title="Penilaian Kinerja" className="mb-9" />

        {isPenetapanPredikatKinerjaEnabled && isOpenAccess && (
          <PredikatKinerjaBanner
            npsn={npsn}
            deadlineDate={formattedDeadlineDate}
          />
        )}

        <div className="bg-white border-gray-30 border rounded">
          <div className="p-6 space-y-6">
            <div className="flex flex-row justify-between items-center">
              <div className="space-y-1 w-6/12">
                <Text variant="heading-md" as="h2">
                  Lakukan Penilaian serta cek status Praktik Kinerja dan
                  Perilaku Kerja
                </Text>
              </div>
              <div className="w-3/12">
                <InputGroup>
                  <InputLeftAddon>
                    <Icon>search</Icon>
                  </InputLeftAddon>
                  <Input
                    placeholder="Cari"
                    value={preSearchText}
                    onKeyDown={(event) => {
                      if (event.key == 'Enter') {
                        setCurrentActiveFilter('all')
                        setCurrentActiveSearch(preSearchText)
                      }
                    }}
                    onChange={(event) => {
                      handleTrackerTypeTeacherName(event)
                      const stripText = event.target.value.replace(
                        /[^a-z0-9]/gi,
                        ''
                      )
                      setPreSearchText(stripText)
                    }}
                  />
                </InputGroup>
              </div>
            </div>
          </div>
          <div className="px-5">
            <FilterButtons
              activeValue={activeFilter}
              items={filterMeta}
              btnSize="sm"
              onChange={(selected) => {
                handleTrackerFilter(selected)

                if (activeSearch) {
                  setCurrentActiveSearch('')
                  setPreSearchText('')
                }
                setCurrentActiveFilter(selected)
              }}
            />
          </div>
          <TablePelaksanaanKinerja>
            <TableHeader>
              <TableHeaderItem>Nama</TableHeaderItem>
              <TableHeaderItem>Status Kepegawaian</TableHeaderItem>
              <TableHeaderItem>
                Jadwal Observasi
                <br />
                Praktik Kinerja
              </TableHeaderItem>
              <TableHeaderItem>
                Status
                <br />
                Praktik Kinerja
              </TableHeaderItem>
              <TableHeaderItem>
                Status
                <br />
                Perilaku Kerja
              </TableHeaderItem>
              <TableHeaderItem />
              <TableHeaderItem />
            </TableHeader>
            <TableBody>
              {isLoadingListGuruStates && <TableShimmer columns={6} />}
              {filteredDataWithSearch.map((thisFilteredData) => {
                const alreadyDone =
                  thisFilteredData?.workBehavior?.gradingState?.toLowerCase() ==
                    ALREADY_GRADED_VALUE &&
                  thisFilteredData?.observation?.gradingState?.toLowerCase() ==
                    DONE_VALUE

                const isFilledBySystem =
                  thisFilteredData.filledBySystem === true
                const isBlocked = thisFilteredData.notAsn === true

                const hasPerencanaan =
                  thisFilteredData?.observation?.date !== NO_DATE_VALUE ||
                  alreadyDone

                return (
                  <TableRow key={`guru_${thisFilteredData.ptkId}`}>
                    <TableRowItem className="overflow-wrap-anywhere">
                      {thisFilteredData.name}
                    </TableRowItem>
                    <TableRowItem>
                      {thisFilteredData.employeeStatus}
                    </TableRowItem>
                    <TableRowItem>
                      {isFilledBySystem
                        ? '-'
                        : hasPerencanaan
                        ? thisFilteredData?.observation?.date
                        : 'Pelaksanaan belum dimulai.'}
                    </TableRowItem>
                    <TableRowItem>
                      {!hasPerencanaan ? (
                        'Beri himbauan untuk segera memulai dan mengisi Diskusi Persiapan.'
                      ) : (
                        <div className="flex items-center space-x-1">
                          <Badge
                            variant={
                              thisFilteredData?.observation?.gradingState?.toLowerCase() ==
                              DONE_VALUE
                                ? 'success'
                                : 'neutral'
                            }
                          >
                            {mapNewGradingStateLabel(
                              thisFilteredData?.observation?.gradingState
                            )}
                          </Badge>

                          {isFilledBySystem && (
                            <Button
                              iconOnly
                              size="sm"
                              className="!bg-transparent"
                              aria-label="Lihat Info"
                              onClick={() => setFilledBySystemDialogOpen(true)}
                            >
                              <Icon>help</Icon>
                            </Button>
                          )}
                        </div>
                      )}
                    </TableRowItem>
                    <TableRowItem>
                      {!thisFilteredData?.workBehavior?.gradingState ? (
                        '-'
                      ) : (
                        <Badge
                          variant={
                            thisFilteredData?.workBehavior?.gradingState?.toLowerCase() ==
                            ALREADY_GRADED_VALUE
                              ? 'success'
                              : 'critical'
                          }
                        >
                          {mapNewGradingStateLabel(
                            thisFilteredData?.workBehavior?.gradingState
                          )}
                        </Badge>
                      )}
                    </TableRowItem>
                    <TableRowItem className="flex flex-row items-center space-x-6">
                      {isBlockNonAsnEnabled && isBlocked ? (
                        <Button
                          size="sm"
                          color={alreadyDone ? 'white' : 'black'}
                          onClick={() => {
                            setBlockNonAsnDialogOpen(true)
                          }}
                        >
                          {alreadyDone ? 'Cek' : 'Lakukan'}
                          <span className="sr-only">
                            {thisFilteredData.name}
                          </span>
                        </Button>
                      ) : (
                        <Link
                          href={`${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}/${thisFilteredData.ptkId}`}
                          passHref
                        >
                          <Button
                            as="a"
                            size="sm"
                            disabled={
                              thisFilteredData?.observation?.date ==
                                NO_DATE_VALUE && !alreadyDone
                            }
                            color={alreadyDone ? 'white' : 'black'}
                            onClick={() => {
                              trackEvent({
                                activity_type: alreadyDone
                                  ? 'penilaian_ks_periksa_skp_click_cek'
                                  : 'penilaian_ks_periksa_skp_click_nilai',
                                screen_name: 'ks_periksa_skp',
                                event_trigger: 'ks_penilaian_hub',
                              })
                            }}
                          >
                            {alreadyDone ? 'Cek' : 'Lakukan'}
                            <span className="sr-only">
                              {thisFilteredData.name}
                            </span>
                          </Button>
                        </Link>
                      )}
                    </TableRowItem>
                    <TableRowItem>
                      {alreadyDone && (
                        <Icon color="success">check_circle_outline</Icon>
                      )}
                    </TableRowItem>
                  </TableRow>
                )
              })}
            </TableBody>
          </TablePelaksanaanKinerja>
          {!isLoadingListGuruStates && listGuruStatesData?.meta?.total == 0 && (
            <EmptyState
              title="Belum ada guru"
              description="Belum ada guru yang sudah menyelesaikan tahap Perencanaan Kinerja. Jika ada, namanya akan tampil di sini."
            />
          )}
          {!isLoadingListGuruStates &&
            filteredDataWithSearch?.length == 0 &&
            listGuruStatesData?.meta?.total > 0 && (
              <EmptyState
                title="Tidak ada hasil"
                description="Pastikan ejaan yang diketik benar dan guru sudah menyelesaikan tahap Perencanaan Kinerja."
              />
            )}
        </div>
      </div>
      {resetEnabled && <ResetButton npsn={npsn} />}

      <DialogFilledBySystem
        isGuru={false}
        isOpen={isFilledBySystemDialogOpen}
        onClose={() => {
          setFilledBySystemDialogOpen(false)
        }}
      />

      <DialogBlockPeriodNonAsn
        isKS={true}
        isOpen={isBlockNonAsnDialogOpen}
        onClose={() => setBlockNonAsnDialogOpen(false)}
      />
    </div>
  )
}

interface EmptyStateProps {
  title: string
  description: string
}

function EmptyState({ title, description }: EmptyStateProps) {
  return (
    <article className="flex justify-center">
      <div className="flex flex-col items-center max-w-md text-center justify-center py-9">
        <Image
          src="/illustrations/img-empty-document-2.png"
          alt=""
          width="84"
          height="84"
        />
        <Text className="mt-3 mb-1" variant="heading-sm">
          {title}
        </Text>
        <Text color="subdued">{description}</Text>
      </div>
    </article>
  )
}

const CONFIRMATION_CHECKLIST: Array<{ label: string; value: string }> = [
  {
    value: '1',
    label:
      'Diskusi dengan Guru bahwa pengiriman ke Dinas Pendidikan bukan tahap akhir dan dilakukan hanya agar Predikat Kinerja Organisasi dapat diproses',
  },
  {
    value: '2',
    label:
      'Data masih dapat Anda ubah setelah Predikat Kinerja Organisasi sudah diproses',
  },
]

function PredikatKinerjaBanner({
  npsn,
  deadlineDate,
}: {
  npsn: string
  deadlineDate: string
}) {
  const { isDemoMode } = useDemoContext()
  const { trackEvent } = useSKPPelaksanaanTrackEvent()

  const [isConfirmationOpen, setConfirmationOpen] = useState(false)
  const [confirmationChecklist, setConfirmationChecklist] = useState<
    Array<string>
  >([])

  const { data: dataPenetapanStep, isLoading: isLoadingPenetapanStep } =
    useAPIGetPenetapanPredikatKinerjaStep({
      params: {
        npsn,
      },
      options: {
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
      },
      isDemoMode,
    })

  const submissionStep = dataPenetapanStep?.data?.submissionStep
  const isCompleted = !!submissionStep && submissionStep !== 'NOT_CREATED'
  const isConfirmed =
    confirmationChecklist.length === CONFIRMATION_CHECKLIST.length

  // Reset confirmation state upon oppening confirmation dialog
  useEffect(() => {
    return () => {
      if (isConfirmationOpen) {
        setConfirmationChecklist([])
      }
    }
  }, [isConfirmationOpen])

  if (isLoadingPenetapanStep) {
    return <Shimmer className="mb-6 !h-24 !w-full" />
  }

  return (
    <>
      <div className="mb-6 bg-white border-gray-30 border rounded">
        <div className="p-6 flex gap-4 items-center">
          <img
            height={84}
            width={84}
            src={assetUrl(
              isCompleted
                ? '/illustrations/img-thumbs-up.png'
                : '/illustrations/img-take-give-mail.png'
            )}
            alt=""
          />
          <div className="flex items-center flex-1 space-x-4">
            <div className="items-center space-y-2 flex-1">
              <Text variant="heading-md">
                {isCompleted
                  ? 'Tahap Penilaian Kinerja sudah selesai!'
                  : 'Kirim data Penilaian Kinerja ke Dinas Pendidikan'}
              </Text>
              <Text variant="eyebrow-md" color="subdued">
                {isCompleted
                  ? 'Data berhasil dikirim ke Dinas Pendidikan. Anda dapat lanjut ke tahap berikutnya.'
                  : 'Data dikirim agar Predikat Kinerja Organisasi satuan pendidikan Anda dapat diproses.'}
              </Text>
              {!isCompleted && (
                <CardDeadlinePeriod deadlineDate={deadlineDate} />
              )}
            </div>

            {isCompleted ? (
              <Link
                href={`${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}`}
                passHref
              >
                <Button
                  color="blue"
                  type={undefined}
                  as="a"
                  onClick={() => {
                    trackEvent({
                      activity_type: 'penilaian_ks_skp_click_penetapan_kinerja',
                      screen_name: 'ks_periksa_skp',
                      event_trigger: 'ks_penilaian_hub',
                    })
                  }}
                >
                  Ke Penetapan Predikat Kinerja
                </Button>
              </Link>
            ) : (
              <Button
                color="blue"
                onClick={() => {
                  trackEvent({
                    activity_type: 'penilaian_ks_skp_click_kirim_data',
                    screen_name: 'ks_periksa_skp',
                    event_trigger: 'ks_penilaian_hub',
                  })

                  setConfirmationOpen(true)
                }}
              >
                Kirim Data Penilaian
              </Button>
            )}
          </div>
        </div>
      </div>
      <Dialog
        isOpen={isConfirmationOpen}
        closeOnOverlayClick={false}
        size="sm"
        onClose={() => setConfirmationOpen(false)}
      >
        <DialogOverlay />
        <DialogContent>
          <div className="p-6 space-y-4">
            <img
              alt=""
              src={assetUrl('/illustrations/img-book-literature-a-z.png')}
              width={96}
              height={96}
            />
            <div className="space-y-1">
              <Text variant="display-sm" as="h2">
                Mohon centang hal-hal berikut:
              </Text>
            </div>
            <CheckboxGroup
              name="syarat"
              className="flex flex-col gap-2"
              value={confirmationChecklist}
              onChange={setConfirmationChecklist}
            >
              {CONFIRMATION_CHECKLIST.map((checkItem) => (
                <Checkbox value={checkItem.value} key={checkItem.value}>
                  {checkItem.label}
                </Checkbox>
              ))}
            </CheckboxGroup>

            <div className="flex space-x-4">
              <Button
                color="white"
                size="lg"
                onClick={() => setConfirmationOpen(false)}
              >
                Kembali
              </Button>
              <Link
                href={`${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}/daftar-guru/rangkuman-penilaian-kinerja`}
                passHref
              >
                <Button
                  as="a"
                  color="blue"
                  size="lg"
                  type={undefined}
                  disabled={!isConfirmed}
                >
                  Ke Kirim Data Penilaian
                </Button>
              </Link>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function ResetButton({ npsn }) {
  const [isOpen, setOpen] = useState(false)
  const mutationResetPenilaian = useAPIPredikatKinerjaReset(npsn)
  const toast = useToast()
  const queryClient = useQueryClient()
  const handleClickSubmitReset = () => {
    mutationResetPenilaian.mutate(
      {},
      {
        onSuccess: () => {
          queryClient.invalidateQueries(`${API_PATH_LIST_GURU_STATES}/${npsn}`)
          queryClient.invalidateQueries(
            `${API_PATH_PREDIKAT_KINERJA_STEP}/${npsn}`
          )
          toast({
            message: 'Berhasil reset penilaian',
          })
        },
        onError: () => {
          toast({ message: 'Gagal reset penilaian' })
        },
        onSettled: () => {
          setOpen(false)
        },
      }
    )
  }
  return (
    <>
      <div className="fixed bottom-0 right-0 p-4 z-50">
        <span className="absolute flex h-3 w-3 right-3 top-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-50" />
        </span>
        <Button
          size="md"
          variant="solid"
          color="red"
          onClick={() => setOpen(true)}
        >
          Reset Penilaian untuk akun testing
        </Button>
      </div>
      <Dialog isOpen={isOpen} onClose={setOpen} size="md">
        <DialogOverlay />
        <DialogContent>
          <DialogHeader
            variant="compact"
            title="Anda yakin melakukan Reset Penilaian?"
          />
          <div className="p-4">
            <Text>
              Melakukan reset penilaian akan menghapus semua penilaian guru di
              satuan pendidikan yang anda pilih
            </Text>
          </div>
          <DialogFooter className="gap-4">
            <DialogCancel
              as={Button}
              color="white"
              onClick={() => setOpen(false)}
            >
              Kembali
            </DialogCancel>
            <DialogAction as={Button} onClick={handleClickSubmitReset}>
              Setuju
            </DialogAction>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
